/**
 * Accessibility Context Provider
 *
 * Provides comprehensive accessibility settings for WCAG 2.1 AA compliance.
 * Implements RFP requirements for:
 * - High contrast modes
 * - Large text/text scaling
 * - Reduced motion support
 * - Screen reader optimizations
 * - Keyboard navigation
 *
 * Settings are persisted to localStorage and applied via CSS custom properties.
 */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from 'react';

// ============================================================================
// ACCESSIBILITY TYPES
// ============================================================================

/**
 * Text size scale options
 */
export type TextScale = '100' | '125' | '150' | '175' | '200';

/**
 * Color contrast mode options
 */
export type ContrastMode = 'normal' | 'high' | 'highest';

/**
 * Color scheme preference
 */
export type ColorScheme = 'light' | 'dark' | 'system';

/**
 * Focus indicator style
 */
export type FocusStyle = 'default' | 'enhanced' | 'high-visibility';

/**
 * Complete accessibility settings
 */
export interface AccessibilitySettings {
  // Visual settings
  textScale: TextScale;
  contrastMode: ContrastMode;
  colorScheme: ColorScheme;

  // Motion settings
  reduceMotion: boolean;
  reduceTransparency: boolean;

  // Focus and navigation
  focusStyle: FocusStyle;
  showFocusOutline: boolean;
  keyboardShortcutsEnabled: boolean;

  // Screen reader settings
  announcePageChanges: boolean;
  verboseLabels: boolean;

  // Reading aids
  dyslexiaFriendlyFont: boolean;
  lineSpacing: 'normal' | 'relaxed' | 'loose';
  letterSpacing: 'normal' | 'wide' | 'wider';

  // Interaction settings
  targetSize: 'normal' | 'large' | 'extra-large';
  showLinkUnderlines: boolean;
}

/**
 * Default accessibility settings
 */
const DEFAULT_SETTINGS: AccessibilitySettings = {
  textScale: '100',
  contrastMode: 'normal',
  colorScheme: 'system',
  reduceMotion: false,
  reduceTransparency: false,
  focusStyle: 'default',
  showFocusOutline: true,
  keyboardShortcutsEnabled: true,
  announcePageChanges: true,
  verboseLabels: false,
  dyslexiaFriendlyFont: false,
  lineSpacing: 'normal',
  letterSpacing: 'normal',
  targetSize: 'normal',
  showLinkUnderlines: true,
};

// ============================================================================
// CONTEXT TYPES
// ============================================================================

interface AccessibilityContextValue {
  settings: AccessibilitySettings;
  updateSettings: (updates: Partial<AccessibilitySettings>) => void;
  resetSettings: () => void;

  // Computed values
  effectiveColorScheme: 'light' | 'dark';
  fontSizeMultiplier: number;

  // Utility functions
  announce: (message: string, priority?: 'polite' | 'assertive') => void;
  skipToContent: () => void;
  openAccessibilityPanel: () => void;
  closeAccessibilityPanel: () => void;

  // Panel state
  isPanelOpen: boolean;
}

// ============================================================================
// CONTEXT
// ============================================================================

const AccessibilityContext = createContext<AccessibilityContextValue | undefined>(undefined);

// ============================================================================
// STORAGE KEY
// ============================================================================

const STORAGE_KEY = 'weavein_accessibility_settings';

// ============================================================================
// CSS CUSTOM PROPERTIES MAPPING
// ============================================================================

const TEXT_SCALE_MAP: Record<TextScale, number> = {
  '100': 1,
  '125': 1.25,
  '150': 1.5,
  '175': 1.75,
  '200': 2,
};

const LINE_SPACING_MAP = {
  normal: '1.5',
  relaxed: '1.75',
  loose: '2',
};

const LETTER_SPACING_MAP = {
  normal: '0',
  wide: '0.05em',
  wider: '0.1em',
};

const TARGET_SIZE_MAP = {
  normal: '44px',
  large: '56px',
  'extra-large': '72px',
};

// ============================================================================
// HIGH CONTRAST COLOR SCHEMES
// ============================================================================

const HIGH_CONTRAST_COLORS = {
  high: {
    light: {
      '--background': '0 0% 100%',
      '--foreground': '0 0% 0%',
      '--primary': '220 100% 30%',
      '--primary-foreground': '0 0% 100%',
      '--secondary': '0 0% 90%',
      '--secondary-foreground': '0 0% 0%',
      '--muted': '0 0% 85%',
      '--muted-foreground': '0 0% 20%',
      '--border': '0 0% 0%',
      '--ring': '220 100% 30%',
    },
    dark: {
      '--background': '0 0% 0%',
      '--foreground': '0 0% 100%',
      '--primary': '220 100% 70%',
      '--primary-foreground': '0 0% 0%',
      '--secondary': '0 0% 20%',
      '--secondary-foreground': '0 0% 100%',
      '--muted': '0 0% 15%',
      '--muted-foreground': '0 0% 80%',
      '--border': '0 0% 100%',
      '--ring': '220 100% 70%',
    },
  },
  highest: {
    light: {
      '--background': '0 0% 100%',
      '--foreground': '0 0% 0%',
      '--primary': '0 0% 0%',
      '--primary-foreground': '60 100% 50%',
      '--secondary': '60 100% 50%',
      '--secondary-foreground': '0 0% 0%',
      '--muted': '60 100% 85%',
      '--muted-foreground': '0 0% 0%',
      '--border': '0 0% 0%',
      '--ring': '0 0% 0%',
    },
    dark: {
      '--background': '0 0% 0%',
      '--foreground': '60 100% 50%',
      '--primary': '60 100% 50%',
      '--primary-foreground': '0 0% 0%',
      '--secondary': '0 0% 20%',
      '--secondary-foreground': '60 100% 50%',
      '--muted': '0 0% 10%',
      '--muted-foreground': '60 100% 50%',
      '--border': '60 100% 50%',
      '--ring': '60 100% 50%',
    },
  },
};

// ============================================================================
// PROVIDER COMPONENT
// ============================================================================

interface AccessibilityProviderProps {
  children: ReactNode;
}

export function AccessibilityProvider({ children }: AccessibilityProviderProps) {
  const [settings, setSettings] = useState<AccessibilitySettings>(() => {
    // Load from localStorage on init
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        try {
          return { ...DEFAULT_SETTINGS, ...JSON.parse(stored) };
        } catch {
          return DEFAULT_SETTINGS;
        }
      }
    }
    return DEFAULT_SETTINGS;
  });

  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [systemColorScheme, setSystemColorScheme] = useState<'light' | 'dark'>('light');

  // Detect system color scheme preference
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    setSystemColorScheme(mediaQuery.matches ? 'dark' : 'light');

    const handler = (e: MediaQueryListEvent) => {
      setSystemColorScheme(e.matches ? 'dark' : 'light');
    };

    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, []);

  // Detect system reduced motion preference
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (mediaQuery.matches && !settings.reduceMotion) {
      setSettings(prev => ({ ...prev, reduceMotion: true }));
    }
  }, []);

  // Persist settings to localStorage
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    }
  }, [settings]);

  // Apply CSS custom properties
  useEffect(() => {
    if (typeof document === 'undefined') return;

    const root = document.documentElement;
    const effectiveScheme =
      settings.colorScheme === 'system' ? systemColorScheme : settings.colorScheme;

    // Text scale - only apply when not default (100%)
    // This prevents overriding the base CSS font-size on desktop
    if (settings.textScale !== '100') {
      root.style.setProperty('--text-scale', TEXT_SCALE_MAP[settings.textScale].toString());
      root.style.fontSize = `${TEXT_SCALE_MAP[settings.textScale] * 100}%`;
    } else {
      // Reset to default - let base CSS handle it
      root.style.removeProperty('--text-scale');
      root.style.removeProperty('font-size');
    }

    // Line spacing - only apply when not default
    if (settings.lineSpacing !== 'normal') {
      root.style.setProperty('--line-height', LINE_SPACING_MAP[settings.lineSpacing]);
    } else {
      root.style.removeProperty('--line-height');
    }

    // Letter spacing - only apply when not default
    if (settings.letterSpacing !== 'normal') {
      root.style.setProperty('--letter-spacing', LETTER_SPACING_MAP[settings.letterSpacing]);
    } else {
      root.style.removeProperty('--letter-spacing');
    }

    // Target size for interactive elements - only apply when not default
    if (settings.targetSize !== 'normal') {
      root.style.setProperty('--min-target-size', TARGET_SIZE_MAP[settings.targetSize]);
    } else {
      root.style.removeProperty('--min-target-size');
    }
    // Set data attribute for CSS selectors (only when user explicitly requests larger targets)
    if (settings.targetSize !== 'normal') {
      root.setAttribute('data-target-size', settings.targetSize);
    } else {
      root.removeAttribute('data-target-size');
    }

    // Color scheme class.
    // Public/unauthenticated pages (e.g. /public/forms/<token>) are hardcoded
    // light-mode in their components, so a system-dark .dark class would make
    // their h1/h3 invisible (via `.dark h1 { color: gray-100 }`) and force
    // native form controls to dark via color-scheme. Force light there.
    root.classList.remove('light', 'dark');
    const isPublicLightOnlyRoute =
      typeof window !== 'undefined' &&
      window.location.pathname.startsWith('/public/');
    const appliedScheme = isPublicLightOnlyRoute ? 'light' : effectiveScheme;
    root.classList.add(appliedScheme);
    root.style.colorScheme = appliedScheme;

    // High contrast mode
    if (settings.contrastMode !== 'normal') {
      const colors = HIGH_CONTRAST_COLORS[settings.contrastMode][effectiveScheme];
      Object.entries(colors).forEach(([prop, value]) => {
        root.style.setProperty(prop, value);
      });
      root.setAttribute('data-contrast', settings.contrastMode);
    } else {
      root.removeAttribute('data-contrast');
      // Remove high contrast colors (let default theme apply)
      Object.keys(HIGH_CONTRAST_COLORS.high.light).forEach(prop => {
        root.style.removeProperty(prop);
      });
    }

    // Reduced motion
    if (settings.reduceMotion) {
      root.classList.add('reduce-motion');
      root.style.setProperty('--transition-duration', '0ms');
      root.style.setProperty('--animation-duration', '0ms');
    } else {
      root.classList.remove('reduce-motion');
      root.style.removeProperty('--transition-duration');
      root.style.removeProperty('--animation-duration');
    }

    // Reduced transparency
    if (settings.reduceTransparency) {
      root.classList.add('reduce-transparency');
    } else {
      root.classList.remove('reduce-transparency');
    }

    // Focus style
    root.setAttribute('data-focus-style', settings.focusStyle);
    if (!settings.showFocusOutline) {
      root.classList.add('hide-focus-outline');
    } else {
      root.classList.remove('hide-focus-outline');
    }

    // Dyslexia-friendly font
    if (settings.dyslexiaFriendlyFont) {
      root.classList.add('dyslexia-font');
      root.style.setProperty('--font-family', 'OpenDyslexic, Arial, sans-serif');
    } else {
      root.classList.remove('dyslexia-font');
      root.style.removeProperty('--font-family');
    }

    // Link underlines
    if (settings.showLinkUnderlines) {
      root.classList.add('show-link-underlines');
    } else {
      root.classList.remove('show-link-underlines');
    }

    // Verbose labels for screen readers
    if (settings.verboseLabels) {
      root.setAttribute('data-verbose-labels', 'true');
    } else {
      root.removeAttribute('data-verbose-labels');
    }
  }, [settings, systemColorScheme]);

  /**
   * Update accessibility settings
   */
  const updateSettings = useCallback((updates: Partial<AccessibilitySettings>) => {
    setSettings(prev => ({ ...prev, ...updates }));
  }, []);

  /**
   * Reset all settings to defaults
   */
  const resetSettings = useCallback(() => {
    setSettings(DEFAULT_SETTINGS);
  }, []);

  /**
   * Announce message to screen readers
   */
  const announce = useCallback((message: string, priority: 'polite' | 'assertive' = 'polite') => {
    if (typeof document === 'undefined') return;

    // Find or create the live region
    let liveRegion = document.getElementById('a11y-announcer');
    if (!liveRegion) {
      liveRegion = document.createElement('div');
      liveRegion.id = 'a11y-announcer';
      liveRegion.setAttribute('role', 'status');
      liveRegion.setAttribute('aria-live', 'polite');
      liveRegion.setAttribute('aria-atomic', 'true');
      liveRegion.className = 'sr-only';
      liveRegion.style.cssText = `
        position: absolute;
        width: 1px;
        height: 1px;
        padding: 0;
        margin: -1px;
        overflow: hidden;
        clip: rect(0, 0, 0, 0);
        white-space: nowrap;
        border: 0;
      `;
      document.body.appendChild(liveRegion);
    }

    // Update priority if needed
    liveRegion.setAttribute('aria-live', priority);

    // Clear and set message (triggers announcement)
    liveRegion.textContent = '';
    requestAnimationFrame(() => {
      liveRegion!.textContent = message;
    });
  }, []);

  /**
   * Skip to main content
   */
  const skipToContent = useCallback(() => {
    if (typeof document === 'undefined') return;

    const main = document.querySelector('main, [role="main"], #main-content');
    if (main instanceof HTMLElement) {
      main.tabIndex = -1;
      main.focus();
      main.scrollIntoView({ behavior: settings.reduceMotion ? 'auto' : 'smooth' });
    }
  }, [settings.reduceMotion]);

  /**
   * Open accessibility settings panel
   */
  const openAccessibilityPanel = useCallback(() => {
    setIsPanelOpen(true);
    announce('Accessibility settings panel opened');
  }, [announce]);

  /**
   * Close accessibility settings panel
   */
  const closeAccessibilityPanel = useCallback(() => {
    setIsPanelOpen(false);
    announce('Accessibility settings panel closed');
  }, [announce]);

  // Computed values
  const effectiveColorScheme =
    settings.colorScheme === 'system' ? systemColorScheme : settings.colorScheme;
  const fontSizeMultiplier = TEXT_SCALE_MAP[settings.textScale];

  const contextValue: AccessibilityContextValue = {
    settings,
    updateSettings,
    resetSettings,
    effectiveColorScheme,
    fontSizeMultiplier,
    announce,
    skipToContent,
    openAccessibilityPanel,
    closeAccessibilityPanel,
    isPanelOpen,
  };

  return (
    <AccessibilityContext.Provider value={contextValue}>
      {children}
    </AccessibilityContext.Provider>
  );
}

// ============================================================================
// HOOKS
// ============================================================================

/**
 * Access accessibility context
 */
export function useAccessibility(): AccessibilityContextValue {
  const context = useContext(AccessibilityContext);
  if (context === undefined) {
    throw new Error('useAccessibility must be used within an AccessibilityProvider');
  }
  return context;
}

/**
 * Get current text scale multiplier
 */
export function useTextScale(): number {
  const { fontSizeMultiplier } = useAccessibility();
  return fontSizeMultiplier;
}

/**
 * Get current color scheme
 */
export function useColorScheme(): 'light' | 'dark' {
  const { effectiveColorScheme } = useAccessibility();
  return effectiveColorScheme;
}

/**
 * Check if reduced motion is enabled
 */
export function useReducedMotion(): boolean {
  const { settings } = useAccessibility();
  return settings.reduceMotion;
}

/**
 * Check if high contrast mode is enabled
 */
export function useHighContrast(): boolean {
  const { settings } = useAccessibility();
  return settings.contrastMode !== 'normal';
}

/**
 * Announce message to screen readers
 */
export function useAnnounce(): (message: string, priority?: 'polite' | 'assertive') => void {
  const { announce } = useAccessibility();
  return announce;
}

export default AccessibilityContext;
